import { CheckCircle2, Circle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';

type Item = {
  id: string;
  title: string;
  hint: string;
  done: boolean;
  to: string;
};

interface OnboardingChecklistProps {
  title?: string;
  items: Item[];
}

export default function OnboardingChecklist({ title = 'Getting Started', items }: OnboardingChecklistProps) {
  const completed = items.filter((i) => i.done).length;
  const total = items.length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  if (total === 0 || completed === total) {
    return null;
  }

  return (
    <Card className="bg-gradient-to-br from-accent2/10 to-glass-2 border border-glass">
      <CardHeader>
        <CardTitle className="text-lg font-bold text-glass-text">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="flex items-center justify-between text-sm text-glass-text-2 mb-1">
            <span>
              {completed} / {total} completed
            </span>
            <span className="font-semibold text-accent2">{pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-glass-input border border-glass overflow-hidden">
            <div
              className="h-full transition-all"
              style={{ width: `${pct}%`, background: 'rgb(var(--accent-rgb))' }}
            />
          </div>
        </div>

        <div className="space-y-2">
          {items.map((item) => (
            <Link
              key={item.id}
              to={item.to}
              className="flex items-start gap-3 p-3 rounded-lg bg-glass-2 border border-glass hover:border-accent2/40 hover:bg-glass-3 transition-all"
            >
              <div className="mt-0.5">
                {item.done ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 dark:text-emerald-400" />
                ) : (
                  <Circle className="w-5 h-5 text-accent2" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-glass-text">{item.title}</p>
                <p className="text-xs text-glass-text-2 mt-0.5">{item.hint}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-accent2 mt-1 shrink-0" />
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
